import React, { useCallback, useMemo } from 'react';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { numberRegex } from 'common/constants/general';
import { MISSING_SECURITIES, MISSING_SECURITIES_MESSAGE } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import LedgerContext, { LedgerContextType } from 'context/LedgerContext';
import { EmptyCapTableMessage } from 'pages/CapTable/cap-table/components';
import { getEditableSecurityValues } from 'pages/Valuations/approaches/backsolveApproach/BacksolveApproach';
import { Logger } from 'utilities';
import { SecurityTableProps } from './type';

const SecurityTable = ({
  spreadsheets,
  onChange,
  setPrevEditableSecurityValues,
  securities,
  securityList,
  deleteRowFn,
  isLedgerTable,
}: SecurityTableProps) => {
  const [format] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, units: REGULAR_UNIT });

  const { securitySheet } = spreadsheets;
  const { isDisabled } = securitySheet.tableData as any;

  const onWorkbookChange = useCallback(
    (cell, value) => {
      // Extract row number using regex to handle multi-digit row numbers (e.g., B10 -> 10)
      const rowNumberMatch = numberRegex.exec(cell.key);
      if (!rowNumberMatch) {
        Logger.error('The row number could not be retrieved from the cell key', { cell });
        return;
      }
      const rowIndex = Number(rowNumberMatch[0]) - 2;
      const auxSecurities = [...securities];
      // to set previous securities values with latest change
      if (cell.alias === 'shares') {
        auxSecurities[rowIndex].shares = value;
      }
      setPrevEditableSecurityValues(getEditableSecurityValues(auxSecurities));
      onChange(cell, value);
    },
    [onChange, securities, setPrevEditableSecurityValues]
  );
  const onCellsChanged = useCallback(
    changes => {
      onWorkbookChange(changes[0].cell, changes[0].value);
    },
    [onWorkbookChange]
  );

  const LedgerContextData = useMemo(
    () => ({
      onCellsChange: onCellsChanged,
      cells: securitySheet.cells,
    }),
    [onCellsChanged, securitySheet]
  ) as LedgerContextType;

  if (securityList && securityList.length === 0) {
    return <EmptyCapTableMessage title={MISSING_SECURITIES} tagline={MISSING_SECURITIES_MESSAGE} />;
  }
  return (
    <LedgerContext.Provider value={LedgerContextData}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore: ScalarSpreadsheet is not typed */}
      <ScalarSpreadsheet
        {...securitySheet}
        id="security-table"
        className={securitySheet.name}
        onChange={onWorkbookChange}
        sheet={securitySheet}
        deleteRowFn={deleteRowFn}
        displayDeleteRowBtn={!isDisabled}
        tableTerms={{ tableSlug: 'security-table' }}
        alwaysDisplayLegend={false}
        isLedgerTable={isLedgerTable}
        format={format}
      />
    </LedgerContext.Provider>
  );
};

export default SecurityTable;
